// DO NOT ADD OR IMPORT ANY JSX HERE

import { sha256 as jsSha256 } from 'js-sha256';
import { useEffect, useRef, useState, useMemo } from 'react';
import { memoize } from './util-no-import';
import { v4 as uuidv4 } from 'uuid';

export async function asyncSha256(message: string) {
    // encode as UTF-8
    const msgBuffer = new TextEncoder().encode(message);
    // hash the message
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    // convert ArrayBuffer to Array
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    // convert bytes to hex string
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

export const sha256 = jsSha256;

if (typeof window !== 'undefined') {
    Object.assign(window, { sha256, asyncSha256 });
}

export const useEffectWithAbort = (effect: (checkAbort?: () => void, abortController?: AbortController) => Promise<void>, deps: React.DependencyList) => {
    useEffect(() => {
        const abortController = new AbortController();
        (async () => {
            let abortError = new Error('Aborted');
            try {
                const checkAbort = () => {
                    if (abortController.signal.aborted) throw abortError;
                };
                await effect(checkAbort, abortController);
            } catch (e) {
                if (e === abortError) return;
                throw e;
            }
        })();
        return () => abortController.abort();
    }, deps);
};

const intersectionObserverOptions = {
    root: null,
    rootMargin: "0px 0px 0px 0px",
    threshold: 0,
};
export const useIsVisible = (elementRef, once = true, enable = true) => {
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const element = elementRef.current;
        if (!element || !enable) return;
        const abortController = new AbortController();
        const observer = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (once && entry.isIntersecting) observer.unobserve(entry.target);
                if (abortController.signal.aborted) return;
                setIsVisible(entry.isIntersecting);
            });
        }, intersectionObserverOptions);
        observer.observe(element);
        return () => {
            observer.unobserve(element);
            abortController.abort();
        };
    }, [elementRef, enable]);
  
    return enable ? isVisible : true;
};

export const useLatestValueRef = <T>(value: T) => {
    const valueRef = useRef(value);
    valueRef.current = value;
    return valueRef;
};

export function useLocalStorage<T>(key: string, initialValue: T) {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
        if (typeof window === "undefined") {
            return initialValue;
        }
        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(key);
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) as T : initialValue;
        } catch (error) {
            // If error also return initialValue
            console.log(error);
            return initialValue;
        }
    });
    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue: typeof setStoredValue = (value) => setStoredValue(storedValue => {
        // Allow value to be a function so we have same API as useState
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        try {
            // Save to local storage
            if (typeof window !== "undefined") {
                window.localStorage.setItem(key, JSON.stringify(valueToStore));
            }
        } catch (error) {
            // A more advanced implementation would handle the error case
            console.log(error);
        }
        return valueToStore;
    });
    return [storedValue, setValue] as const;
}

export const useIsUnmountedRef = () => {
    const isUnmountedRef = useRef(false);
    useEffect(() => {
        return () => {
            isUnmountedRef.current = true;
        };
    }, []);
    return isUnmountedRef;
};

export const getIsDebug = memoize(() => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1');

export const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState(() => ({
        width: window.innerWidth,
        height: window.innerHeight,
    }));
    useEffect(() => {
        const onResize = () => {
            setWindowSize(oldSize => {
                const newSize = {
                    width: window.innerWidth,
                    height: window.innerHeight,
                };
                if (oldSize.width === newSize.width && oldSize.height === newSize.height) {
                    return oldSize;
                }
                return newSize;
            });
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return windowSize;
};

const resizeObserverHandlers: WeakMap<Element, (rect: DOMRectReadOnly) => void> = new WeakMap();
const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver((entries) => {
    for (const entry of entries) {
        const handler = resizeObserverHandlers.get(entry.target);
        if (handler) {
            handler(entry.contentRect);
        }
    }
}) : undefined;
export const useResizeOberserver = (el: Element) => {
    const prevObserved = useRef<Element | void>(undefined);
    const [size, setSize] = useState<DOMRectReadOnly | void>(undefined);
    useEffect(() => {
        if (el) {
            if (prevObserved.current && prevObserved.current !== el) {
                resizeObserver.unobserve(prevObserved.current);
                prevObserved.current = undefined;
            }
            resizeObserverHandlers.set(el, (contentRect) => {
                setSize(contentRect);
            });
            resizeObserver.observe(el);
            prevObserved.current = el;
        }
        return () => {
            if (prevObserved.current) {
                resizeObserver.unobserve(prevObserved.current);
                prevObserved.current = undefined;
            }
        };
    }, [el]);
    return size;
};

export const memoDebug = (() => {
    const list: { key: String, weakRef: WeakRef<any> }[] = [];
    const memoDebug = {
        add(key: string, value: any) {
            if (typeof WeakRef === 'undefined') return;
            // eslint-disable-next-line no-undef
            const weakRef = new WeakRef(value);
            list.push({
                key,
                weakRef,
            });
        },
        get() {
            return list.filter(o => o.weakRef.deref()).map(o => o.key);
        },
    };
    if (typeof window !== 'undefined') Object.assign(window, { memoDebug });
    return memoDebug;
})();

export function Memo(props: {
        children: () => any;
        deps?: any[];
}) {
    const comp = useMemo(props.children, props.deps || []);
    return comp;
}

export const useLatestCallback = <T>(fn: T extends ((...args: any[]) => any) ? T : any): T => {
    const fnRef = useLatestValueRef(fn);
    const memoFn = useMemo(() => ((...args) => fnRef.current(...args)) as T, [fnRef]);
    return memoFn;
};

const objectToUuidWeakMap: WeakMap<object, string> = new WeakMap();
export const objectToUuid = (obj: object) => {
    let id = objectToUuidWeakMap.get(obj);
    if (!id) {
        id = uuidv4();
        objectToUuidWeakMap.set(obj, id);
    }
    return objectToUuidWeakMap.get(obj);
};
